import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";
import { registerServiceWorker } from "@/registerSW";
import { installSwAnalyticsBridge } from "@/lib/swAnalyticsBridge";
import { initStatusBarOverlay } from "@/lib/statusBar";
import { bootstrapNativePasskey } from "@/lib/nativePasskeyBootstrap";
import { initSentry, Sentry } from "@/lib/sentry";

// Init Sentry error tracking BEFORE rendering — silent no-op when
// REACT_APP_SENTRY_DSN is unset or NODE_ENV=development.
initSentry();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

// Fallback UI shown when a React render error escapes all inner handlers.
// Kept dead-simple + Arabic + RTL so it looks native even if the crash
// happened deep inside a lazy chunk. Users get a "reload" button because
// most render errors clear on refresh.
function CrashFallback() {
  return (
    <div
      dir="rtl"
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#0b1120",
        color: "#f8fafc",
        fontFamily: "Tajawal, system-ui, sans-serif",
        padding: 24,
        textAlign: "center",
      }}
    >
      <div>
        <h1 style={{ fontSize: 24, marginBottom: 12 }}>حَدَث خَطأ غَير مُتَوَقَّع</h1>
        <p style={{ opacity: 0.85, marginBottom: 20 }}>
          تَمَّ إخطار فَريقنا تِلقائياً. يُرجى إعادة تَحميل الصَّفحة.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            background: "#f59e0b",
            color: "#0b1120",
            border: 0,
            padding: "10px 22px",
            fontSize: 15,
            fontWeight: 600,
            borderRadius: 8,
            cursor: "pointer",
          }}
        >
          إعادة التَّحميل
        </button>
      </div>
    </div>
  );
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<CrashFallback />}>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);

registerServiceWorker();
installSwAnalyticsBridge();
initStatusBarOverlay();
// Patch navigator.credentials on iOS/Android native so Face ID / Touch ID
// work inside the WKWebView. No-op on web (Safari, Chrome).
bootstrapNativePasskey();
